import api from '@api/api';
import { Permission } from '@models/permission';
import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import { IRootState } from '@store/reducers';

interface PermissionSliceState {
  permissionList: Permission[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: PermissionSliceState = {
  permissionList: [],
  status: 'idle',
  error: null,
};

export const fetchPermissionList = createAsyncThunk(
  'permissionSlice/fetchPermissionList',
  async () => {
    const {
      data: { data, code, msg },
    } = await api.post('/api/config-service/permission/user');

    if (code !== 0) {
      throw Error(`code: ${code} ${msg}`);
    }
    return data;
  },
);

const permissionSlice = createSlice({
  name: 'permissionSlice',
  initialState,
  reducers: {
    setPermissionList: (state, action) => {
      state.permissionList = action.payload;
    },
    clearPermissionList: (state) => {
      state.permissionList = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPermissionList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(
        fetchPermissionList.fulfilled,
        (state, action: PayloadAction<Permission[]>) => {
          state.status = 'succeeded';
          state.permissionList = action.payload;
          localStorage.setItem(
            'permissionList',
            JSON.stringify(state.permissionList),
          );
        },
      )
      .addCase(fetchPermissionList.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || null;
      });
  },
});

// 為了整理出父子關係，所以 arr 只會剩第一級
// 第一級裡的 children 下就是第二級，第二級裡的 children 就是第三級
function transformPermissionsToTree(
  permissions: Permission[],
  pid: number | null = null,
): Permission[] {
  return permissions
    .filter((permission) => permission.pid === pid)
    .map((permission) => ({
      ...permission,
      children: transformPermissionsToTree(permissions, permission.id),
    }));
}

// 將從後端拿到攤平的權限進行整理，先濾掉 BUTTON 類型（目前 BUTTON 沒有任何用處）
// 再濾掉會被 engMode 影響，再用 transformPermissionsToTree 整理出父子關係

// localStorage 的 engMode 為 true 時，菜單權限就要保留會被 engMode 影響的權限（affectedByEngMode: true）
// localStorage 的 engMode 不為 true 時，菜單權限就要過濾掉會被 engMode 影響（affectedByEngMode: true）
export const originMenuMap = createSelector(
  (state: IRootState) => state.permissionSlice.permissionList,
  (permissionList: Permission[]) => {
    const routeList: Permission[] = permissionList
      .filter((permission) => permission.type !== 'BUTTON')
      .filter(
        (permission) =>
          !(
            permission.affectedByEngMode &&
            localStorage.getItem('engMode') !== 'true'
          ),
      );

    const lastLevelRouteList = transformPermissionsToTree(routeList);
    return lastLevelRouteList;
  },
);

// 為了組第三級的 path，跟過濾掉一二級沒有 children 的
export const newMenuMap = createSelector(
  originMenuMap,
  (lastLevelRouteList: Permission[]) => {
    function addPathParams(
      permissions: Permission[],
      parentPath = '',
    ): Permission[] {
      return permissions
        .flatMap((permission) => {
          // 建立當前菜單項目的路徑
          const currentPath = parentPath
            ? `${parentPath}/${permission.name}`
            : permission.name;

          // 遞迴處理子菜單項目，傳遞當前菜單項目的路徑作為子菜單項目的父路徑
          const children: Permission[] | undefined = permission.children
            ? addPathParams(permission.children, currentPath)
            : [];

          // 若為 FIRST_LEVEL_MENU 或 SECOND_LEVEL_MENU 且沒有子項目，則跳過此菜單項目
          if (
            (permission.type === 'FIRST_LEVEL_MENU' ||
              permission.type === 'SECOND_LEVEL_MENU') &&
            children.length === 0
          ) {
            return [];
          }

          // 對於第三級菜單，使用 currentPath 作為路徑參數
          const pathParam =
            permission.type === 'THIRD_LEVEL_MENU' ? currentPath : undefined;

          // 返回更新后的权限项，包括计算得出的 pathParam
          return {
            ...permission,
            path: pathParam,
            children,
          };
        })
        .filter((item) => item);
    }

    return addPathParams(lastLevelRouteList);
  },
);

export const { setPermissionList, clearPermissionList } =
  permissionSlice.actions;
export default permissionSlice.reducer;
